import { EllipsisOutlined, SearchOutlined } from '@ant-design/icons';
import type { TablePaginationConfig } from 'antd';
import {
  Badge,
  Button,
  Dropdown,
  Input,
  Layout,
  PageHeader,
  Pagination,
  Table,
} from 'antd';
import type { TableProps } from 'antd/es/table';
import type { FilterValue, SortOrder } from 'antd/es/table/interface';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useHideHotel } from 'app/hooks/data/core/admin/hotels/useHideHotel';
import { useAdminHotelsList } from 'app/hooks/data/core/admin/hotels/useHotelsList';
import { usePublishHotel } from 'app/hooks/data/core/admin/hotels/usePublishHotel';
import { useUnpublishHotel } from 'app/hooks/data/core/admin/hotels/useUnpublishHotel';
import { useAdminUser } from 'app/hooks/data/useAdminUser';
import { HotelStatus } from 'app/typings';
import type { AdminHotelListItem } from 'app/typings/core/admin/hotels/hotelsList';
import { dateAndHour } from 'app/utils/dates';
import { extractMissingFields } from 'app/utils/publishError';
import { capitalizeFirstLetter } from 'app/utils/string';

import { RedirectIcon } from './RedirectIcon';
import './index.scss';

const { Content } = Layout;
const { Column } = Table;

const MODE = [
  {
    text: 'Old',
    value: 'OLD',
  },
  {
    text: 'New',
    value: 'NEW',
  },
];

const STATUS = [
  {
    text: capitalizeFirstLetter(HotelStatus.PUBLISHED.toLowerCase()),
    value: HotelStatus.PUBLISHED,
  },
  {
    text: capitalizeFirstLetter(HotelStatus.UNPUBLISHED.toLowerCase()),
    value: HotelStatus.UNPUBLISHED,
  },
  {
    text: capitalizeFirstLetter(HotelStatus.HIDDEN.toLowerCase()),
    value: HotelStatus.HIDDEN,
  },
];

const LIMIT = 50;

type TableParams = {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: SortOrder;
  filters?: Record<string, FilterValue | null>;
};

export const HotelsList = () => {
  const history = useHistory();
  const location = useLocation();
  const { isSuperAdmin } = useContext(UserContext);
  const searchParams = new URLSearchParams(location.search);
  const { data: hotels, isLoading: loading } = useAdminHotelsList();
  const { data: admins, isLoading: adminsLoading } = useAdminUser();
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get('search') ?? ''
  );
  const { mutateAsync: hideHotel } = useHideHotel();
  const { mutateAsync: publishHotel } = usePublishHotel();
  const { mutateAsync: unpublishHotel } = useUnpublishHotel();

  const [missingFieldsError, setMissingFieldsError] = useState<string[] | null>(
    null
  );
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const [tableDataSize, setTableDataSize] = useState<number>(0);
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({
    isLegacy: ['OLD', 'NEW'],
    status: [
      HotelStatus.HIDDEN,
      HotelStatus.PUBLISHED,
      HotelStatus.UNPUBLISHED,
    ],
  });

  useEffect(() => {
    if (hotels && !loading) {
      setTableDataSize(hotels.length);
    }
  }, [hotels, loading]);

  const getInitialTableParams = () => {
    setFilteredInfo(
      searchParams.get('filters')
        ? JSON.parse(searchParams.get('filters') as string)
        : { mode: filteredInfo.mode, status: filteredInfo.status }
    );

    return {
      pagination: {
        current: searchParams.get('page')
          ? parseInt(searchParams.get('page') as string, 10)
          : 1,
        total: tableDataSize,
        pageSize: LIMIT,
        showQuickJumper: false,
        showSizeChanger: false,
        showLessItems: true,
      },
      sortField: searchParams.get('sortField') || undefined,
      sortOrder: (searchParams.get('sortOrder') as SortOrder) || undefined,
      filters: searchParams.get('filters')
        ? JSON.parse(searchParams.get('filters') as string)
        : { mode: filteredInfo.mode, status: filteredInfo.status },
    };
  };

  const [tableParams, setTableParams] = useState<TableParams>(
    getInitialTableParams
  );

  useEffect(() => {
    const params = new URLSearchParams({
      search: searchTerm || '',
      page: tableParams.pagination?.current?.toString() || '1',
      sortField: tableParams.sortField || '',
      sortOrder: tableParams.sortOrder || '',
      filters: JSON.stringify(tableParams.filters) || '{}',
    });

    history.replace({
      search: params.toString(),
    });
  }, [tableParams, searchTerm, history]);

  const handleRowActions = (hotel: AdminHotelListItem) => ({
    onClick: () => history.push(`/hotels/${hotel.id}/edit`),
  });

  const formatDate = (date: string) => date && dateAndHour(date);

  const pagination = (className: string) => (
    <div className={className}>
      <Pagination
        {...tableParams.pagination}
        total={tableDataSize ?? 1}
        onChange={handleChangePage}
      />
    </div>
  );

  const header = () => (
    <div className="body-header">
      <div className="left">
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setTableDataSize(filteredHotels?.length ?? 0);
          }}
          suffix={<SearchOutlined />}
          className="search"
          size="large"
        />
      </div>
      {pagination('button-wrapper')}
    </div>
  );

  const newHotelsButton = (
    <Button
      type="primary"
      size="large"
      onClick={() => history.push('hotels/new')}
    >
      New
    </Button>
  );

  const renderStatusCell = (status: HotelStatus) => {
    if (status === HotelStatus.PUBLISHED) {
      return <Badge status="success" text="Published" />;
    } else if (status === HotelStatus.UNPUBLISHED) {
      return <Badge status="warning" text="Unpublished" />;
    }

    return <Badge color="#bfbfbf" text="Hidden" />;
  };

  const handleTableChange: TableProps<AdminHotelListItem>['onChange'] = (
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    pagination,
    filters,
    sorter,
    size
  ) => {
    if (!Array.isArray(sorter)) {
      setFilteredInfo(filters);
      setTableParams({
        pagination,
        filters,
        sortField: sorter?.field as string,
        sortOrder: sorter?.order as SortOrder,
      });
      setTableDataSize(size.currentDataSource.length || 0);
    }
  };

  const handleChangePage = (page: number) => {
    setTableParams((oldValue) => ({
      ...oldValue,
      pagination: {
        ...oldValue.pagination,
        current: page,
      },
    }));
  };

  const managerIds = hotels
    ?.map((obj) => obj.managerId)
    .filter((id) => id !== undefined);

  const filterOwner =
    admins
      ?.filter((user) => managerIds?.includes(user.id))
      ?.map((admin) => ({
        text:
          admin.firstName && admin.lastName
            ? `${admin.firstName} ${admin.lastName}`
            : admin.email,
        value: admin.id.toString(),
      })) ?? [];

  const sortOwner = (a: number, b: number) => {
    const personA = admins?.find((admin) => admin.id === a);
    const personB = admins?.find((admin) => admin.id === b);

    const isUndefinedA = !personA?.firstName || !personA?.lastName;
    const isUndefinedB = !personB?.firstName || !personB?.lastName;

    if (!isUndefinedA && !isUndefinedB) {
      const fullNameA = `${personA?.firstName} ${personA?.lastName}`;
      const fullNameB = `${personB?.firstName} ${personB?.lastName}`;

      return fullNameA.localeCompare(fullNameB);
    }

    if (isUndefinedA && !isUndefinedB) {
      return 1;
    }

    if (!isUndefinedA && isUndefinedB) {
      return -1;
    }

    return 0;
  };

  const renderOwner = (id?: number) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const admin = admins?.find((admin) => admin.id === id);

    if (admin) {
      return admin.firstName && admin.lastName
        ? `${admin.firstName} ${admin.lastName}`
        : admin.email;
    }

    return '';
  };

  const renderHotelRedirect = (lineData: AdminHotelListItem) => {
    return (
      <Button
        icon={<RedirectIcon />}
        onClick={() =>
          window.open(`/extranet/inventory?hotelId=${lineData.id}`, '_blank')
        }
      />
    );
  };

  const filteredHotels = hotels
    ?.sort((a, b) => {
      let comparison = 0;

      if (tableParams.sortField && tableParams.sortOrder) {
        const fieldA = a[tableParams.sortField as keyof AdminHotelListItem];
        const fieldB = b[tableParams.sortField as keyof AdminHotelListItem];

        if (fieldA && fieldB) {
          comparison = fieldA > fieldB ? 1 : fieldA < fieldB ? -1 : 0;

          return tableParams.sortOrder === 'ascend' ? comparison : -comparison;
        } else if (fieldA && !fieldB) {
          return tableParams.sortOrder === 'ascend' ? -1 : 1;
        } else if (!fieldA && fieldB) {
          return tableParams.sortOrder === 'ascend' ? 1 : -1;
        }
      }

      return comparison;
    })
    .filter((hotel) => {
      if (!searchTerm) {
        return true;
      }

      const lowerSearchTerm = searchTerm.toLowerCase();
      const hotelName = `${hotel.name || ''}`.toLowerCase();
      const hotelId = `${hotel.id || ''}`.toLowerCase();
      const manager = renderOwner(hotel.managerId).toLowerCase();

      return (
        hotelName.includes(lowerSearchTerm) ||
        hotelId.includes(lowerSearchTerm) ||
        manager.includes(lowerSearchTerm)
      );
    });

  const onPublishClick = async (hotel: AdminHotelListItem) => {
    if (hotel.status === HotelStatus.PUBLISHED) {
      return unpublishHotel({ hotelId: hotel.id });
    }

    try {
      await publishHotel({ hotelId: hotel.id });
    } catch (err) {
      const missingFields = extractMissingFields(err);

      if (missingFields !== null) {
        setMissingFieldsError(missingFields);
        setIsErrorModalVisible(true);
      }
    }
  };

  const onHideClick = (hotel: AdminHotelListItem) => {
    if (hotel.status === HotelStatus.HIDDEN) {
      return unpublishHotel({ hotelId: hotel.id });
    }

    return hideHotel({ hotelId: hotel.id });
  };

  const actionMenu = (hotel: AdminHotelListItem) => (
    <div className="actions-menu">
      <Dropdown
        menu={{
          items: [
            {
              key: 'publish',
              label:
                hotel.status === HotelStatus.PUBLISHED
                  ? 'Unpublish'
                  : 'Publish',
              disabled: hotel.status === HotelStatus.HIDDEN,
              onClick: async () => await onPublishClick(hotel),
            },
            {
              key: 'hide',
              label: hotel.status === HotelStatus.HIDDEN ? 'Show' : 'Hide',
              disabled: hotel.status === HotelStatus.PUBLISHED || !isSuperAdmin,
              onClick: async () => await onHideClick(hotel),
            },
          ],
        }}
        trigger={['click']}
      >
        <EllipsisOutlined rotate={90} />
      </Dropdown>
    </div>
  );

  return (
    <Layout className="page-list">
      <PageHeader className="header" title="Hotels" extra={newHotelsButton} />
      <Content className="body">
        <Table
          dataSource={filteredHotels ?? []}
          pagination={{ ...tableParams.pagination, total: tableDataSize }}
          title={header}
          loading={loading || adminsLoading}
          rowKey={(record) => record.id}
          onRow={handleRowActions}
          onChange={handleTableChange}
        >
          <Column
            key="name"
            title="Name"
            dataIndex="name"
            defaultSortOrder={
              tableParams.sortField === 'name'
                ? tableParams.sortOrder
                : undefined
            }
            sorter={(a: AdminHotelListItem, b: AdminHotelListItem) =>
              a.name.localeCompare(b.name)
            }
          />
          <Column
            key="id"
            title="Id"
            dataIndex="id"
            sorter={(a: AdminHotelListItem, b: AdminHotelListItem) =>
              a.id - b.id
            }
            defaultSortOrder={
              tableParams.sortField === 'id' ? tableParams.sortOrder : undefined
            }
          />
          <Column
            key="owner"
            title="Owner"
            dataIndex="managerId"
            defaultSortOrder={
              tableParams.sortField === 'managerId'
                ? tableParams.sortOrder
                : undefined
            }
            sorter={(a: AdminHotelListItem, b: AdminHotelListItem) =>
              sortOwner(a.managerId ?? 0, b.managerId ?? 0)
            }
            render={(adminId) => renderOwner(adminId)}
            filters={[...filterOwner, { text: 'No owner', value: 'noOwner' }]}
            filteredValue={filteredInfo.owner || null}
            onFilter={(
              value: string | number | boolean,
              record: AdminHotelListItem
            ) => {
              if (value === 'noOwner') {
                return record.managerId === undefined;
              }

              if (record.managerId && record.managerId.toString() === value) {
                return true;
              }

              return false;
            }}
            defaultFilteredValue={
              tableParams.sortField === 'owner'
                ? tableParams.filters?.owner
                : null
            }
            filterSearch={true}
          />
          <Column
            key="isLegacy"
            title="Data Model"
            dataIndex="isLegacy"
            defaultSortOrder={
              tableParams.sortField === 'isLegacy'
                ? tableParams.sortOrder
                : undefined
            }
            filters={MODE}
            defaultFilteredValue={
              tableParams.sortField === 'isLegacy'
                ? tableParams.filters?.isLegacy
                : null
            }
            filteredValue={filteredInfo.isLegacy || null}
            onFilter={(
              value: string | number | boolean,
              record: AdminHotelListItem
            ) => {
              if (value === 'OLD') {
                return record.isLegacy === true;
              } else if (value === 'NEW') {
                return record.isLegacy === false;
              }

              return true;
            }}
            render={(isLegacy: boolean) => (isLegacy ? 'Old' : 'New')}
          />
          <Column
            key="status"
            title="Published"
            dataIndex="status"
            filters={STATUS}
            defaultFilteredValue={tableParams.filters?.status}
            filteredValue={filteredInfo.status || null}
            onFilter={(
              value: string | number | boolean,
              record: AdminHotelListItem
            ) => record.status === value}
            render={renderStatusCell}
          />
          <Column
            key="updatedAt"
            title="Last modified"
            dataIndex="updatedAt"
            sorter={(a: AdminHotelListItem, b: AdminHotelListItem) =>
              new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
            }
            render={formatDate}
            defaultSortOrder={
              tableParams.sortField === 'updatedAt'
                ? tableParams.sortOrder
                : undefined
            }
          />
          <Column
            render={(_, lineData: AdminHotelListItem) =>
              renderHotelRedirect(lineData)
            }
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
          <Column
            render={actionMenu}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <PublishModalError
          type={PUBLISH_ERROR_TYPE.HOTEL}
          missingFields={missingFieldsError ?? []}
          isOpen={isErrorModalVisible}
          handleOk={() => {
            setIsErrorModalVisible(false);
            setMissingFieldsError(null);
          }}
        />
      </Content>
    </Layout>
  );
};
